import PageLayout from "@components/core/layouts/PageLayout";
import CardSection from "@components/core/section/CardSection";
import { ROUTES } from "@routes/Routes";
import {
  setCertificateStatus,
  setEmailAndLastnameAndFirstname,
  setOnboardingError,
} from "@state/auth/AuthEvents";
import { authenticationStore } from "@state/auth/AuthStore";
import { degradedStore } from "@state/degraded/DegradedStore";
import { DegradedModeDTO } from "@state/degraded/dto/response/degraded.mode.dto";
import { LogoutDetailsDto } from "@state/logoutDetails/dto/response/logout.details.mode.dto";
import { logoutDetailsStore } from "@state/logoutDetails/LogoutDetailsStore";
import {
  requestGetCertificateStatus,
  requestOnboardingLogout,
  requestUpdateCertificateStatus,
  requestUpdateUserWithNewValue,
} from "@state/onboarding/OnboardingEffects";
import { AuthenticationContext } from "@type/core/auth/auth.types";
import DateFormat from "@utils/DateFormat";
import { StatusEnum } from "@utils/enums/status.enum";
import { toastError } from "@utils/helpers/toast-helper";
import OBDisconnectPage from "@views/core/extranet/onboarding/pages/OBDisconnectPage";
import OBHomePage from "@views/core/extranet/onboarding/pages/OBHomePage";
import OBInProgressPage from "@views/core/extranet/onboarding/pages/OBInProgressPage";
import OBNewInformationPage from "@views/core/extranet/onboarding/pages/OBNewInformationPage";
import OBSignatureDelegationSoonExpirationPage from "@views/core/extranet/onboarding/pages/OBSignatureDelegationSoonExpirationPage";
import OBSuccessPage from "@views/core/extranet/onboarding/pages/OBSuccessPage";
import OBUsSoonExpirationPage from "@views/core/extranet/onboarding/pages/OBUsSoonExpirationPage";
import { useUnit } from "effector-react";
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";

const OnboardingScreen: FunctionComponent = () => {
  const { t } = useTranslation();
  const rawNavigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [pageLoading, setPageLoading] = useState<boolean>(true);
  const [certificateStatusLoading, setCertificateStatusLoading] =
    useState<boolean>(true);
  const [obPage, setObPage] = useState<number>(1);

  const [
    isHomeOnboardingPhoneErrorModalOpen,
    setIsHomeOnboardingPhoneErrorModalOpen,
  ] = useState<boolean>(false);
  const [
    isUsSoonExpirationInformationModalOpen,
    setIsUsSoonExpirationInformationModalOpen,
  ] = useState<boolean>(false);
  const [
    isUsSoonExpirationRenewUsCertificateModalOpen,
    setIsUsSoonExpirationRenewUsCertificateModalOpen,
  ] = useState<boolean>(false);
  const [
    isUsSoonExpirationReconnectionNeededModalOpen,
    setIsUsSoonExpirationReconnectionNeededModalOpen,
  ] = useState<boolean>(false);
  const [
    isNewInformationReconnectionNeededModalOpen,
    setIsNewInformationReconnectionNeededModalOpen,
  ] = useState<boolean>(false);
  const [
    isNewInformationConsentRevocationModalOpen,
    setIsNewInformationConsentRevocationModalOpen,
  ] = useState<boolean>(false);

  const authStore = useUnit<AuthenticationContext | null>(authenticationStore);
  const degradedModeStore = useUnit<DegradedModeDTO | null>(degradedStore);
  const logoutDetails = useUnit<LogoutDetailsDto | null>(logoutDetailsStore);
  const certificateDegradedMode: boolean =
    degradedModeStore?.certificateDegradedMode ?? false;
  const status = useParams().status;
  const certificateStatus = authStore?.user?.tsCertificateStatus;
  const usExpirationDate = useMemo(
    () => new Date(authStore?.user?.usExpirationDate as string),
    [authStore?.user?.usExpirationDate],
  );
  const signatureDelegationusExpirationDate = new Date(
    authStore?.user?.signatureDelegationusExpirationDate as string,
  );

  const isMounted = useRef(false);
  const navigateOnFirstRendering = useRef<string | null>(null);
  const prevCertificateStatus = useRef(certificateStatus);

  const navigate = (to: string) => {
    if (isMounted.current) {
      rawNavigate(to);
    } else {
      // navigate could not be called on first rendering
      navigateOnFirstRendering.current = to;
    }
  };

  useEffect(() => {
    if (navigateOnFirstRendering.current !== null) {
      navigate(navigateOnFirstRendering.current);
    }
  }, []);

  const handleProgressStatus = useCallback(() => {
    if (certificateDegradedMode) {
      navigate("/public/universignDegraded");
    }
    setObPage(6);
  }, [certificateDegradedMode, navigate]);

  const handleValidStatus = useCallback(() => {
    if (authStore?.user?.onboardingError === "NEW_EMAIL") {
      setObPage(8);
    } else if (authStore?.user?.signatureDelegationusExpirationDate) {
      setObPage(10);
    } else if (DateFormat.certificateDateWithMonth(usExpirationDate)) {
      setObPage(9);
    } else {
      setObPage(3);
    }
  }, [
    authStore?.user?.onboardingError,
    authStore?.user?.signatureDelegationusExpirationDate,
    usExpirationDate,
  ]);

  const handleInvalidStatus = useCallback(() => {
    if (authStore?.user?.onboardingError === "NEW_EMAIL") {
      setObPage(8);
    } else {
      if (certificateDegradedMode) {
        navigate("/public/universignDegraded");
      }
      setObPage(1);
    }
  }, [authStore, certificateDegradedMode, navigate]);

  const handleStartedStatus = useCallback(() => {
    if (authStore?.user?.onboardingError === "NEW_EMAIL") {
      setObPage(8);
    }
  }, [authStore]);

  const handleDefaultStatus = useCallback(() => {
    if (!isMounted.current) {
      if (certificateDegradedMode) {
        navigate("/public/universignDegraded");
      }
      setObPage(1);
    }
  }, [certificateDegradedMode, navigate]);

  const handleCertificateStatusByEnum = useCallback(() => {
    switch (certificateStatus) {
      case StatusEnum.PROGRESS:
        handleProgressStatus();
        break;
      case StatusEnum.VALID:
        handleValidStatus();
        break;
      case StatusEnum.INVALID:
        handleInvalidStatus();
        break;
      case StatusEnum.STARTED:
        handleStartedStatus();
        break;
      default:
        handleDefaultStatus();
        break;
    }
  }, [
    certificateStatus,
    handleProgressStatus,
    handleValidStatus,
    handleInvalidStatus,
    handleStartedStatus,
    handleDefaultStatus,
  ]);

  const handleCertificateStatus = () => {
    if (status === "processing") {
      void requestUpdateCertificateStatus({}).then((res) => {
        if (!res.ok) {
          toastError(t("onboarding.home.updateCertificateStatusError"));
        }
      });
      navigate(ROUTES.extranet.onboarding.generate());
    } else if (authStore?.user?.disconnectRedirection) {
      setObPage(7);
    } else if (!isMounted.current && !authStore?.user?.onboarding) {
      navigate(ROUTES.extranet.home.generate());
    } else {
      handleCertificateStatusByEnum();
    }
  };

  if (!isMounted.current) {
    if (status !== "processing") {
      setCertificateStatusLoading(true);
      void requestGetCertificateStatus({})
        .then((res) => {
          if (res.ok) {
            setCertificateStatus(res.data?.status as StatusEnum);
          } else {
            toastError(t("onboarding.home.getCertificateStatusError"));
          }
        })
        .finally(() => {
          setCertificateStatusLoading(false);
        });
    }
    handleCertificateStatus();
    setPageLoading(false);
  }

  if (
    prevCertificateStatus.current !== certificateStatus &&
    authStore?.user?.inOrganization
  ) {
    prevCertificateStatus.current = certificateStatus;

    handleCertificateStatus();
  }

  useEffect(() => {
    return requestOnboardingLogout.done.watch(({ result }) => {
      if (result.ok && logoutDetails) {
        location.href =
          logoutDetails.urlCasOnboarding +
          "?origin=" +
          logoutDetails.urlConnexion +
          window.location.pathname;
      } else {
        toastError(t("onboarding.redirect.logoutError"));
      }
    });
  }, [logoutDetails]);

  useEffect(() => {
    return requestUpdateUserWithNewValue.done.watch(({ result }) => {
      if (result.ok && result.data) {
        setEmailAndLastnameAndFirstname({
          email: result.data?.email,
          lastname: result.data?.lastName,
          firstname: result.data?.firstName,
        });
        setCertificateStatus(result.data?.tsCertificateStatus);
        setOnboardingError(result.data?.onboardingError);
        navigate(ROUTES.extranet.home.generate());
        setLoading(false);
      } else {
        toastError(t("onboarding.newInformation.updateUserWithNewValueError"));
      }
    });
  }, [navigate, setLoading]);

  const getTitle = (): string => {
    return t(
      `onboarding.title.${
        authStore?.user?.onboardingError
          ? authStore?.user?.onboardingError
          : certificateStatus === StatusEnum.STARTED
            ? "STARTED"
            : "default"
      }`,
    );
  };

  // call at last to be sure that the component is completely mounted, only rendering must occur after this line
  if (!isMounted.current) {
    isMounted.current = true;
  }

  return (
    <PageLayout
      isLoading={loading || pageLoading || certificateStatusLoading}
      bodyClassName="mt-0"
    >
      <CardSection
        title={
          obPage === 1
            ? getTitle()
            : obPage === 8
              ? t("onboarding.newInformation.title")
              : undefined
        }
        className={obPage === 1 || obPage === 8 ? "" : "mt-48"}
      >
        <>
          {obPage === 1 && (
            <OBHomePage
              user={authStore?.user}
              setLoading={setLoading}
              isOnboardingPhoneErrorModalOpen={
                isHomeOnboardingPhoneErrorModalOpen
              }
              setIsOnboardingPhoneErrorModalOpen={
                setIsHomeOnboardingPhoneErrorModalOpen
              }
            />
          )}
          {obPage === 3 && <OBSuccessPage />}
          {obPage === 6 && <OBInProgressPage />}
          {obPage === 7 && <OBDisconnectPage />}
          {obPage === 8 && (
            <OBNewInformationPage
              certificateDegradedMode={certificateDegradedMode}
              setLoading={setLoading}
              isReconnectionNeededModalOpen={
                isNewInformationReconnectionNeededModalOpen
              }
              setIsReconnectionNeededModalOpen={
                setIsNewInformationReconnectionNeededModalOpen
              }
              isConsentRevocationModalOpen={
                isNewInformationConsentRevocationModalOpen
              }
              setIsConsentRevocationModalOpen={
                setIsNewInformationConsentRevocationModalOpen
              }
            />
          )}
          {obPage === 9 && (
            <OBUsSoonExpirationPage
              usExpirationDate={usExpirationDate}
              certificateDegradedMode={certificateDegradedMode}
              setLoading={setLoading}
              isInformationModalOpen={isUsSoonExpirationInformationModalOpen}
              setIsInformationModalOpen={
                setIsUsSoonExpirationInformationModalOpen
              }
              isRenewUsCertificateModalOpen={
                isUsSoonExpirationRenewUsCertificateModalOpen
              }
              setIsRenewUsCertificateModalOpen={
                setIsUsSoonExpirationRenewUsCertificateModalOpen
              }
              isReconnectionNeededModalOpen={
                isUsSoonExpirationReconnectionNeededModalOpen
              }
              setIsReconnectionNeededModalOpen={
                setIsUsSoonExpirationReconnectionNeededModalOpen
              }
            />
          )}
          {obPage === 10 && (
            <OBSignatureDelegationSoonExpirationPage
              logoutDetails={logoutDetails}
              signatureDelegationusExpirationDate={
                signatureDelegationusExpirationDate
              }
            />
          )}
        </>
      </CardSection>
    </PageLayout>
  );
};

export default OnboardingScreen;
